import Analytics, { type AnalyticsInstance, type AnalyticsPlugin } from 'analytics'
import type { AnalyticsFacade } from './types.js'
import type { EventName, EventProps } from './events.js'
import type { PersistentTags } from './tags.js'

export interface CreateAnalyticsOptions {
  app: string
  plugins: AnalyticsPlugin[]
}

/**
 * Thin typed facade over the npm `analytics` instance. Persistent dimensions
 * ride on identify() traits (re-sent on change, idempotent); funnel steps ride
 * on track() properties. The underlying `analytics` instance dispatches each
 * call to its registered plugins (e.g. clarityPlugin) exactly once.
 */
export function createAnalytics(opts: CreateAnalyticsOptions): AnalyticsFacade {
  const instance: AnalyticsInstance = Analytics({ app: opts.app, plugins: opts.plugins })
  return {
    identify(id: string, traits?: PersistentTags): void {
      void instance.identify(id, traits ?? {})
    },
    track<E extends EventName>(event: E, props?: EventProps[E]): void {
      void instance.track(event, props ?? {})
    },
    page(): void {
      void instance.page()
    },
    setRecording(enabled: boolean): void {
      // Recording control is provider-specific (Clarity start/stop) and is NOT
      // an `analytics` lifecycle event, so we call it directly on each plugin
      // that opts in rather than dispatching through the instance. Plugins
      // without setRecording (e.g. the no-op admin build with no plugins) are
      // skipped — the facade method stays a safe no-op there.
      for (const plugin of opts.plugins) {
        const fn = (plugin as { setRecording?: (e: boolean) => void }).setRecording
        if (typeof fn === 'function') {
          try { fn(enabled) } catch { /* fail-soft: analytics must never break the app */ }
        }
      }
    },
  }
}
